import { FeatureFlag } from '@ps/web-core-enums';
import { useFeature } from '@ps/web-core-store';
import { getWindow } from '@ps/web-core-utils';
import type { Entity } from '@ps/web-shared-analytics';
import { deposit as depositContext, useTrackers } from '@ps/web-shared-analytics';
import { Translation } from '@ps/web-shared-components';
import { addGlobalContexts, removeGlobalContexts } from '@snowplow/browser-tracker';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactGA from 'react-ga';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router';

import { GADimension } from '@swf/core/enums';
import useWizardContext from '@swf/core/hooks/useWizardContext';
import useWizardExit from '@swf/core/hooks/useWizardExit';
import type { Dispatch } from '@swf/core/store';
import { select } from '@swf/core/store';

import { CesFeedbackFormFlowType, type HelpSection } from '../../../../types/Cashier';
import { CardType } from '../../../../types/cardType';
import { DepositStatus } from '../../../../types/status';
import { isRedirectStatus } from '../../../../utils/cashier';
import { getSubtitle, getTitle } from '../../../../utils/title';
import CesFeedbackForm from '../../../CesFeedbackForm/CesFeedbackForm';
import Container from '../../../Container/Container';
import { isBankTransfer } from '../../data';
import HelpSectionContent from '../DepositResult/components/HelpSectionContent/HelpSectionContent';

import {
  TXTWCL_Deposit_methods_help,
  TXTWCL_Help_with_deposits,
  TXTWCL_Please_return_to_the_Cashier_a_ELL,
  TXTWCL_You_can_check_the_status_of_yo_ELL
} from './DepositResult.messages.json';
import AnotherInProgress from './components/AnotherInProgress/AnotherInProgress';
import AuthenticationRequired from './components/AuthenticationRequired/AuthenticationRequired';
import Completed from './components/Completed/Completed';
import DepositResultCTA from './components/DepositResultCTA/DepositResultCTA';
import DepositUnavailable from './components/DepositUnavailable/DepositUnavailable';
import PartiallyCompletedCheck from './components/PartiallyCompletedCheck/PartiallyCompletedCheck';
import PayNearMeRedirect from './components/PayNearMeRedirect/PayNearMeRedirect';
import PayconiqOpenAppFailed from './components/PayconiqOpenAppFailed/PayconiqOpenAppFailed';
import Redirect from './components/Redirect/Redirect';
import RejectMaxDepositsLimitExceeded from './components/RejectMaxDepositsLimitExceeded/RejectMaxDepositsLimitExceeded';
import Rejected from './components/Rejected/Rejected';
import RejectedX3dSecure2Redirect from './components/RejectedX3dSecure2Redirect/RejectedX3dSecure2Redirect';
import RejectedX3dSecure2RequiredError from './components/RejectedX3dSecure2RequiredError/RejectedX3dSecure2RequiredError';
import Timeout from './components/Timeout/Timeout';
import Unknown from './components/Unknown/Unknown';

const DepositResult: React.FC = () => {
  const {
    data: {
      signupPassed,
      rmfPassed,
      depositResult,
      depositResultContext,
      previousDeposit,
      deposit,
      googlePayNativeResultError,
      cmsHeaderAndGridLayout
    }
  } = useWizardContext();
  const contexts = useRef({} as Entity);
  const embedded = useSelector(select.device.isEmbedded);
  const isMobileEmbedded = useSelector(select.device.isMobileEmbedded);
  const isLimitedAccount = useSelector(select.account.isLimitedAccount);
  const isSwcSignupRmfEcopokerLobby = useFeature(FeatureFlag.SwcSignupRmfEcopokerLobby);
  const { pathname } = useLocation();
  const { pageView } = useTrackers();
  const exitWizard = useWizardExit();
  const [resultUpdated, setResultUpdated] = useState(false);
  const { amount, currency, type } = deposit || {};
  const [showTransitionalExitPrompt, setShowTransitionalExitPrompt] = useState(false);
  const handleStatusChange = useCallback(() => setResultUpdated(true), [setResultUpdated]);
  const transactionId = depositResult?.transactionId || depositResultContext?.transactionId || '';
  const isSwcDepositCesEnabled = useFeature(FeatureFlag.SwcDepositCes);
  const skipTouchpoint =
    !isSwcDepositCesEnabled || depositResult?.status !== DepositStatus.COMPLETED;
  const { modal } = useDispatch<Dispatch>();
  const isDeLugasReregistration: boolean = useFeature(FeatureFlag.DeLugasReregistration);

  useEffect(() => {
    if (depositResult?.status && type === CardType.ApplePay && embedded) {
      window.starsweb?.applepay?.completePayment?.(
        depositResult?.status === DepositStatus.COMPLETED
      );
    }
  }, [depositResult?.status, embedded, type]);

  useEffect(() => {
    removeGlobalContexts([contexts.current]);

    return () =>
      ReactGA.set({
        [GADimension.DepositStatus]: null,
        [GADimension.DepositRedirectStatus]: null,
        [GADimension.DepositGateway]: null
      });
  }, []);

  useEffect(() => {
    if (resultUpdated) {
      addGlobalContexts([contexts.current]);
      pageView(pathname);
    }

    return () => removeGlobalContexts([contexts.current]);
  }, [resultUpdated, depositResult, pageView, pathname]);

  if (!depositResult && !googlePayNativeResultError) {
    return null;
  }

  contexts.current = depositContext({
    amount: +amount,
    currency,
    status: (depositResult?.status || googlePayNativeResultError?.status) as string,
    paySystemCode: +type
  });

  const handleOnCompletedStatus = () => {
    setShowTransitionalExitPrompt(true);
  };

  const getDepositResultPage = () => {
    if (googlePayNativeResultError) {
      ReactGA.set({
        [GADimension.DepositStatus]: googlePayNativeResultError.status
      });

      return <Rejected deposit={deposit} {...googlePayNativeResultError} />;
    }
    if (depositResult) {
      const { processor, redirectStatus, status } = depositResult;

      ReactGA.set({
        [GADimension.DepositStatus]: status,
        ...(redirectStatus ? { [GADimension.DepositRedirectStatus]: redirectStatus } : {}),
        ...(processor ? { [GADimension.DepositGateway]: processor } : {})
      });

      switch (status) {
        case DepositStatus.REDIRECT:
          return type === CardType.PayNearMe ? (
            <PayNearMeRedirect
              deposit={deposit}
              showPrompt={showTransitionalExitPrompt}
              {...depositResult}
            />
          ) : (
            <Redirect
              deposit={deposit}
              cmsHeaderAndGridLayout={!!cmsHeaderAndGridLayout}
              handleStatusChange={handleStatusChange}
              {...depositResult}
            />
          );
        case DepositStatus.REJECTED_X3D_SECURE_REQUIRED_ERROR:
          return (
            <Redirect
              deposit={deposit}
              cmsHeaderAndGridLayout={!!cmsHeaderAndGridLayout}
              handleStatusChange={handleStatusChange}
              {...depositResult}
            />
          );
        case DepositStatus.COMPLETED:
          return (
            <Completed
              deposit={deposit}
              previousDeposit={previousDeposit}
              cmsHeaderAndGridLayout={!!cmsHeaderAndGridLayout}
              showPrompt={showTransitionalExitPrompt}
              {...depositResult}
            />
          );
        case DepositStatus.REJECTED:
        case DepositStatus.REJECTED_PEP_HIO_THIRD_PARTY:
          return <Rejected deposit={deposit} {...depositResult} transactionId={transactionId} />;
        case DepositStatus.ERRORS:
        case DepositStatus.REJECTED_CARD_BLOCKED:
          return <DepositUnavailable {...depositResult} />;
        case DepositStatus.ANOTHER_IN_PROGRESS:
          return <AnotherInProgress deposit={deposit} {...depositResult} />;
        case DepositStatus.TIMEOUT:
        case DepositStatus.PRECASHING_TIMEOUT:
          return <Timeout deposit={deposit} {...depositResult} />;
        case DepositStatus.PARTIALLY_COMPLETED_CHECK:
          return <PartiallyCompletedCheck deposit={deposit} {...depositResult} />;
        case DepositStatus.PAYCONIQ_OPEN_APP_FAILED:
          return <PayconiqOpenAppFailed />;
        case DepositStatus.REJECTED_MAX_DEPOSITS_LIMIT_EXCEEDED:
          return <RejectMaxDepositsLimitExceeded deposit={deposit} {...depositResult} />;
        case DepositStatus.AUTHENTICATION_REQUIRED:
          return <AuthenticationRequired />;
        case DepositStatus.UNKNOWN_TRANSACTION_ID:
          return <Translation {...TXTWCL_Please_return_to_the_Cashier_a_ELL} />;
        case DepositStatus.MISSING_TRANSACTION_ID:
          return <Translation {...TXTWCL_You_can_check_the_status_of_yo_ELL} />;
        case DepositStatus.REJECTED_X3D_SECURE2_REDIRECT:
          return (
            <RejectedX3dSecure2Redirect
              deposit={deposit}
              previousDeposit={previousDeposit}
              depositResult={depositResult}
              cmsHeaderAndGridLayout={!!cmsHeaderAndGridLayout}
            />
          );
        case DepositStatus.REJECTED_X3D_SECURE2_REQUIRED_ERROR:
          return (
            <RejectedX3dSecure2RequiredError
              deposit={deposit}
              previousDeposit={previousDeposit}
              updateFdmsStatus={handleStatusChange}
              {...depositResult}
            />
          );
        case isDeLugasReregistration && DepositStatus.ERR_LUGAS_PLAYER_IS_NOT_REGISTERED:
          embedded
            ? getWindow()?.starsweb?.app.execute('cross-operator-deposit-limit-notif')
            : modal.enqueue({
                path: '/notifications/cross-operator-deposit-limit'
              });
          return;
        default:
          return <Unknown {...depositResult} />;
      }
    }
  };

  const status = (): DepositStatus => {
    if (depositResult) {
      return depositResult.status;
    } else if (googlePayNativeResultError) {
      return googlePayNativeResultError.status;
    } else {
      return DepositStatus.REJECTED;
    }
  };

  const isFinalStatus = !isRedirectStatus(status());
  const isPayconiqTimeout = status() === DepositStatus.TIMEOUT && type === CardType.Payconiq;
  const isPayNearMeRedirect = status() === DepositStatus.REDIRECT && type === CardType.PayNearMe;
  const showTopTitle =
    isFinalStatus ||
    status() === DepositStatus.REJECTED_X3D_SECURE2_REQUIRED_ERROR ||
    isPayNearMeRedirect;

  const helpSection: HelpSection = {
    title: isPayconiqTimeout ? TXTWCL_Deposit_methods_help : TXTWCL_Help_with_deposits,
    body: <HelpSectionContent isPayconiqTimeout={isPayconiqTimeout} status={status()} />
  };

  return (
    <Container
      page={DepositResult}
      headerProps={{
        title: getTitle(status(), deposit, isBankTransfer(deposit?.type)),
        buttons: isMobileEmbedded
          ? {
              onCloseClick: exitWizard
            }
          : {},
        showExitPrompt: !isMobileEmbedded
      }}
      pageTopTitle={
        showTopTitle && getSubtitle(status(), isPayconiqTimeout, isPayNearMeRedirect, deposit?.type)
      }
      hideBonusSummary
      showFooter
      helpSupportResolver
      updateAnalyticsContext={() => addGlobalContexts([contexts.current])}
      helpSection={
        [DepositStatus.COMPLETED, DepositStatus.MISSING_TRANSACTION_ID].includes(status()) ||
        isPayconiqTimeout
          ? helpSection
          : undefined
      }
    >
      {getDepositResultPage()}
      <CesFeedbackForm flow={CesFeedbackFormFlowType.Deposit} skipFeedbackForm={skipTouchpoint} />
      {isFinalStatus && (
        <DepositResultCTA
          status={status()}
          isBankTransfer={isBankTransfer(deposit?.type)}
          isPayconiqTimeout={isPayconiqTimeout}
          {...(!isLimitedAccount &&
            isSwcSignupRmfEcopokerLobby &&
            signupPassed &&
            rmfPassed && { onClick: handleOnCompletedStatus })}
        />
      )}
    </Container>
  );
};

DepositResult.displayName = 'DepositResult';

export default DepositResult;
